import { QueryObserverOptions, useQuery } from "react-query";
import { APIError, APIResponse, API_URL, get } from "../api";
import { normalizeStorageUrl } from "../helpers/assetUrl";

export interface MerchantListItem {
  id: string;
  business_name: string | null;
  logo: string | null;
  product_count: number;
  acronym?: string | null;
}

export interface MerchantPaginationMeta {
  current_page: number;
  last_page: number;
  total: number;
  per_page?: number;
}

type MerchantResponseData =
  | MerchantListItem[]
  | {
      data: MerchantListItem[];
      meta?: MerchantPaginationMeta;
    };

export const hasUsableMerchantLogo = (logo?: string | null) => {
  const logoUrl = normalizeStorageUrl(logo).trim();

  return (
    !!logoUrl &&
    logoUrl !== "https://app.visaro.ng/storage/" &&
    logoUrl !== "https://staging.visaro.ng/storage/"
  );
};

function useMerchants(
  params?: { page?: number; perPage?: number },
  options?: QueryObserverOptions<APIResponse<MerchantResponseData>, APIError>
) {
  const searchParams = new URLSearchParams();
  if (params?.page) {
    searchParams.set("page", String(params.page));
  }
  if (params?.perPage) {
    searchParams.set("per_page", String(params.perPage));
  }
  const queryString = searchParams.toString();
  const {
    data: response,
    isError,
    error,
    isLoading,
  } = useQuery<APIResponse<MerchantResponseData>, APIError>(
    ["all-merchants", params?.page ?? 1, params?.perPage ?? 20],
    () =>
      get(API_URL + `/commerce/merchants${queryString ? `?${queryString}` : ""}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
    options
  );
  const responseData = response?.data;
  const rawMerchants = Array.isArray(responseData)
    ? responseData
    : responseData?.data ?? [];
  const merchants = rawMerchants.filter((merchant) =>
    hasUsableMerchantLogo(merchant.logo)
  );
  const pagination = !Array.isArray(responseData)
    ? responseData?.meta
    : (response?.meta as MerchantPaginationMeta | undefined);

  return {
    response,
    rawMerchants,
    merchants,
    pagination,
    isError,
    error,
    isLoading,
  };
}

export default useMerchants;
